import React, { Fragment, useState, useEffect } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import theme from "../../theme";
import { ProjectTypeObj, Types, UserTypeId } from "../../common/const";
import CommonDialog from "../../common/components/CommonDialog";
import { Close, Description } from "@mui/icons-material";
import RichTextEditor from "react-rte";
import { updateProjectResearch } from "../../api/resourceApi";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonEditor from "../../common/components/CommonEditor";
import CommonFormField from "../../common/components/CommonFormField";
import Dropzone from "react-dropzone";

const Details = ({
    fromMentor,
    projectData,
    getProjectDetails
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [fileNames, setFileNames] = useState([]);
    const [editingProject, setEditingProject] = useState(null);
    const [value, setValue] = useState(null);
    const [newProject, setNewProject] = useState({
        project_name: '',
        total_hours: '',
        description: '',
        goal: RichTextEditor?.createValueFromString(value || '', 'html'),
    });

    const getImages = async () => {
        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${projectData?.images?.url}&download=false`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const blob = await responseData.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewUrl(imageUrl || '');
    }

    useEffect(() => {
        if (projectData?.images?.url) {
            setPreviewUrl(null);
            getImages();
        }
    }, [projectData]);

    const handleOpenDialog = (project = null) => {
        setEditingProject(project);
        setFileNames(project?.images || []);
        setNewProject({
            project_name: project?.project_name || '',
            description: project?.description || '',
            goal: RichTextEditor?.createValueFromString(project?.goal || '', 'html')
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setEditingProject(null);
        setValue('');
        setNewProject({
            project_name: '',
            description: '',
            goal: RichTextEditor?.createValueFromString('', 'html')
        });
        setOpenDialog(false);
    };

    const handleSaveProject = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const formData = new FormData();
            formData.append('project_name', newProject?.project_name);
            formData.append('description', newProject?.description);
            formData.append('start_date', new Date(projectData?.start_date)?.toISOString());
            formData.append('status', projectData?.status);
            formData.append('project_type', Types[0].value);
            formData.append('project_source', projectData?.project_source);
            formData.append('project_profile', projectData?.project_profile);
            if (!fileNames.filename) {
                formData.append('images', fileNames[0]);
            }
            formData.append('goal', newProject.goal?._cache?.html);
            const response = await updateProjectResearch(editingProject.project_id, formData);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setFileNames([]);
                getProjectDetails();
            }
            handleCloseDialog();
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };


    return (
        <Fragment>
            <CommonDialog
                open={openDialog}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Edit Project</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    handleCloseDialog();
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={12}>
                            <Box sx={{ width: '100%' }}>
                                {fileNames?.length > 0 ? (
                                    // Show selected files
                                    <Box sx={{ mt: 2 }}>
                                        {fileNames.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Description color="primary" />
                                                    <Box>
                                                        <Typography variant="body1">{file.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                    color="error"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : fileNames?.filename ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                borderRadius: 1,
                                                mb: 1,
                                                backgroundColor: 'background.paper'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Description color="primary" />
                                                <Box>
                                                    <Typography variant="body1">{fileNames.filename}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(fileNames.size / 1024).toFixed(2)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => setFileNames([])}
                                                color="error"
                                            >
                                                <Close />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ) : (
                                    // Show dropzone when no files
                                    <Dropzone
                                        onDrop={handleDrop}
                                        accept='.png,.jpg,.jpeg'
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                sx={{
                                                    border: '2px dashed',
                                                    borderColor: '#D8DAEB',
                                                    borderRadius: 2,
                                                    padding: { xs: 3, md: 6 },
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    minHeight: 100,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <img src="/Images/sound.png" alt="upload" />
                                                <Typography fontWeight={600} gutterBottom>
                                                    Drag and drop your files
                                                </Typography>
                                                <CommonButton
                                                    label="Select File"
                                                    color="Black"
                                                    background="White"
                                                    border={`1px solid ${theme.palette.background.secondary}`}
                                                    width="100px"
                                                    height="40px"
                                                    marginBottom="0px"
                                                    marginTop="10px"
                                                />
                                            </Box>
                                        )}
                                    </Dropzone>
                                )}
                            </Box>
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Project Name"
                                type="email"
                                name="name"
                                value={newProject?.project_name}
                                onChange={(e) => setNewProject((prev) => ({
                                    ...prev,
                                    project_name: e.target.value
                                }))}
                                placeholder="Enter Project Name"
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="description"
                                value={newProject?.description}
                                onChange={(e) => setNewProject((prev) => ({
                                    ...prev,
                                    description: e.target.value
                                }))}
                                placeholder="Enter Description"
                                multiline
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonEditor
                                placeholder="Enter the goal"
                                value={newProject.goal}
                                setValue={(value) => {
                                    setValue(value.toString('html'));
                                    setNewProject({ ...newProject, goal: value })
                                }}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label={editingProject ? 'Update Project' : 'Save Project'}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        disabled={!newProject?.project_name?.trim() || !newProject?.description?.trim() || fileNames.length === 0}
                        handleClick={() => {
                            handleSaveProject();
                        }}
                    />
                )}
            />
            <CommonCard
                borderRadius={2}
                paddingBottom="0px"
                content={
                    <Fragment>
                        <Typography fontWeight={600}>{projectData?.project_name}</Typography>
                        <Box display="flex" justifyContent="space-between" mt={1} width="100%">
                            <Box display="flex" gap={1} width="100%">
                                <img
                                    src={previewUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(`${projectData?.project_name}`)}&background=FAFAFAf1&color=fff`}
                                    alt="project"
                                    width="200px"
                                    height="190px"
                                    style={{ borderRadius: 10 }}
                                />
                                <Box>
                                    <Typography color="secondary.main" fontSize={14}>{projectData?.project_source === ProjectTypeObj.dcp ? 'DCP Project' : 'External Project'}</Typography>
                                    <Typography fontWeight={600}>{projectData?.project_name}</Typography>
                                    <Typography fontSize={14} color="background.dark">{projectData?.description}</Typography>
                                    <CommonCard
                                        paddingBottom="0px"
                                        padding="12px"
                                        borderRadius={2}
                                        marginTop={0.8}
                                        background={theme.palette.background.main}
                                        content={
                                            <Fragment>
                                                <Typography fontSize={14} fontWeight={600}>Goal</Typography>
                                                <Typography
                                                    fontSize={14}
                                                    color={theme.palette.background.dark}
                                                    textAlign="justify"
                                                    dangerouslySetInnerHTML={{ __html: projectData?.goal }}
                                                />
                                            </Fragment>
                                        }
                                    />
                                    <Box display="flex" gap={1} mt={1}>
                                        {projectData?.categories.map((a, index) => (
                                            <Box key={index} sx={{ background: '#EFF6FF', p: '2px 14px', borderRadius: 1 }}>
                                                <Typography color="secondary.main" fontSize={14}>{a}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                            {!fromMentor && user?.user_type_id === UserTypeId.Student && (
                                <CommonButton
                                    label="Edit"
                                    marginBottom="0px"
                                    marginTop="0px"
                                    background="White"
                                    color="black"
                                    border={`1px solid #0000001A`}
                                    width="60px"
                                    height="30px"
                                    handleClick={() => handleOpenDialog(projectData)}
                                />
                            )}
                        </Box>
                    </Fragment>
                }
            />
        </Fragment>
    )
}

export default Details;